// Base API configuration and utilities
import { getApiUrl } from '@/config/runtime-env'
import { getAccessToken, getTokens, saveTokens, clearTokens, type Tokens } from '@/utils/tokenService'
import { API_ENDPOINTS } from '@/utils/constants'

const API_BASE_URL = getApiUrl()

class ApiError extends Error {
  constructor(public status: number, message: string, public data?: any) {
    super(message)
    this.name = 'ApiError'
  }
}

// Refresh lock to prevent multiple simultaneous refresh attempts
let refreshPromise: Promise<Tokens> | null = null

// Emit custom events for refresh state
function emitRefreshEvent(isRefreshing: boolean) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('tokenRefreshing', { 
      detail: { isRefreshing } 
    }))
  }
}

// Refresh tokens function
async function refreshTokens(): Promise<Tokens> {
  // If already refreshing, return the existing promise
  if (refreshPromise) {
    return refreshPromise
  }

  const current = getTokens()
  if (!current?.refreshToken) {
    throw new Error("No refresh token available")
  }

  // Emit event that refresh is starting
  emitRefreshEvent(true)

  // Create the refresh promise
  refreshPromise = (async () => {
    try {
      const response = await fetch(API_ENDPOINTS.AUTH.REFRESH_TOKEN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken: current.refreshToken })
      })

      if (!response.ok) {
        throw new Error("Refresh token invalid or expired")
      }

      const responseData = await response.json()
      const data = responseData.data || responseData
      
      if (!data.accessToken) {
        throw new Error("No access token in refresh response")
      }

      const newTokens: Tokens = {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken || current.refreshToken // Use new refresh token if provided
      }
      saveTokens(newTokens)
      return newTokens
    } catch (error) {
      // Only clear tokens if it's a definitive auth failure
      if (error instanceof Error && 
          (error.message.includes("invalid") || 
           error.message.includes("expired"))) {
        clearTokens()
      }
      throw error
    } finally {
      // Clear the refresh promise after completion
      refreshPromise = null
      // Emit event that refresh is complete
      emitRefreshEvent(false)
    }
  })()

  return refreshPromise
}

// Get auth token from localStorage
const getAuthToken = (): string | null => {
  return getAccessToken()
}

// Create headers with auth token
const createHeaders = (additionalHeaders?: HeadersInit, skipContentType?: boolean): HeadersInit => {
  const token = getAuthToken()
  const baseHeaders: Record<string, string> = {
    ...(token && { 'Authorization': `Bearer ${token}` })
  }
  
  // Don't set Content-Type for FormData - let browser set it with boundary
  if (!skipContentType) {
    baseHeaders['Content-Type'] = 'application/json'
  }
  
  return {
    ...baseHeaders,
    ...additionalHeaders
  }
}

// Base fetch wrapper with error handling and automatic token refresh
export const apiFetch = async <T>(
  endpoint: string,
  options: RequestInit & { skipContentType?: boolean; retryCount?: number } = {}
): Promise<T> => {
  const url = `${API_BASE_URL}${endpoint}`
  const retryCount = options.retryCount || 0
  
  const config: RequestInit = {
    ...options,
    headers: createHeaders(options.headers, options.skipContentType)
  }

  try {
    const response = await fetch(url, config)
    
    if (!response.ok) {
      // Handle 401 errors with token refresh
      if (response.status === 401 && retryCount === 0) {
        console.log('[apiFetch] Got 401, attempting token refresh...')
        try {
          await refreshTokens()
          console.log('[apiFetch] Token refreshed successfully, retrying request...')
          // Retry with new token
          return apiFetch<T>(endpoint, { ...options, retryCount: 1 })
        } catch (refreshError) {
          console.error('[apiFetch] Token refresh failed:', refreshError)
          // Only throw auth error if refresh truly failed
          if (refreshError instanceof Error && 
              (refreshError.message.includes("invalid") || 
               refreshError.message.includes("expired"))) {
            const errorResponse = await response.json().catch(() => ({}))
            const errorData = errorResponse.data || errorResponse
            throw new ApiError(
              response.status,
              errorData.message || 'Authentication failed',
              errorData
            )
          }
          // For network errors during refresh, throw original error
        }
      }
      
      const errorResponse = await response.json().catch(() => ({}))
      const errorData = errorResponse.data || errorResponse
      throw new ApiError(
        response.status,
        errorData.message || `HTTP ${response.status}: ${response.statusText}`,
        errorData
      )
    }

    const data = await response.json()
    return data.data || data // Handle both wrapped and unwrapped responses
  } catch (error) {
    if (error instanceof ApiError) {
      throw error
    }
    throw new ApiError(0, 'Network error or invalid response', error)
  }
}

// HTTP method helpers
export const api = {
  get: <T>(endpoint: string, params?: Record<string, any>) => {
    let url = endpoint
    if (params) {
      // Filter out undefined/null values and convert to strings
      const filteredParams = Object.entries(params)
        .filter(([_, value]) => value !== undefined && value !== null && value !== '')
        .reduce((acc, [key, value]) => {
          acc[key] = String(value)
          return acc
        }, {} as Record<string, string>)
      
      if (Object.keys(filteredParams).length > 0) {
        url = `${endpoint}?${new URLSearchParams(filteredParams)}`
      }
    }
    return apiFetch<T>(url, { method: 'GET' })
  },

  post: <T>(endpoint: string, data?: any, options?: { skipContentType?: boolean }) =>
    apiFetch<T>(endpoint, {
      method: 'POST',
      body: data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined),
      skipContentType: data instanceof FormData || options?.skipContentType
    }),

  patch: <T>(endpoint: string, data?: any) =>
    apiFetch<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined
    }),

  put: <T>(endpoint: string, data?: any) =>
    apiFetch<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    }),

  delete: <T>(endpoint: string) =>
    apiFetch<T>(endpoint, { method: 'DELETE' })
}

export { ApiError }